import React from 'react';
import { FadingWrapper } from '../../../components/FadingWrapper';
import { ContinueButton } from '../../../components/ContinueButton';
import { useRsi } from '../../../hooks/useRsi';
import { Box, Grid, Typography, styled } from '@beeline.com/bp-design-system';
import { ModalBody } from '../../../components/ModalBody';
import { Column, Columns } from '../types';

type ColumnGridProps<T extends string> = {
  columns: Columns<T>;
  userColumn: (column: Column<T>) => React.ReactNode;
  templateColumn: (column: Column<T>) => React.ReactNode;
  onContinue: (val: Record<string, string>[]) => void;
  onBack?: () => void;
  isLoading: boolean;
  constrainBox?: boolean;
};

export const ColumnGrid = <T extends string>({
  columns,
  userColumn,
  templateColumn,
  onContinue,
  onBack,
  isLoading,
  constrainBox
}: ColumnGridProps<T>) => {
  const { translations } = useRsi();

  return (
    <>
      <Typography variant="h2" pb={2}>
        <span data-test="title">{translations.matchColumnsStep.title}</span>
        <Box gridColumn={getHeaderGridColumn(columns)}>
          <StyledSubTitle variant="body1">{translations.matchColumnsStep.userTableTitle}</StyledSubTitle>
          <Typography variant="label1" pt={4}>
            {translations.matchColumnsStep.userTableSubTitle}
          </Typography>
        </Box>
      </Typography>
      <ModalBody constrainBox={constrainBox}>
        <Grid
          flex={1}
          display="grid"
          gridTemplateRows="auto auto auto 1fr"
          gridTemplateColumns={`repeat(${columns.length}, minmax(18rem, ${100 / columns.length}%))`}
        >
          {columns.map((column, index) => (
            <Box gridRow="2/3" gridColumn={getItemGridColumn(index)} pt={1} key={column.header + index}>
              {userColumn(column)}
            </Box>
          ))}
          <FadingWrapper gridColumn={getHeaderGridColumn(columns)} gridRow="2/3" />
        </Grid>
        <Grid
          display="grid"
          container
          gridTemplateRows="auto auto auto 1fr"
          gridTemplateColumns={`repeat(${columns.length}, minmax(18rem, ${100 / columns.length}%))`}
        >
          <Box gridColumn={getHeaderGridColumn(columns)} mt={7}>
            <Typography variant="label1">{translations.matchColumnsStep.templateTitle}</Typography>
          </Box>
          {columns.map((column, index) => (
            <Box gridRow="4/5" gridColumn={getItemGridColumn(index)} key={column.header + index} py={4} px={2}>
              {templateColumn(column)}
            </Box>
          ))}
        </Grid>
      </ModalBody>
      <ContinueButton
        isLoading={isLoading}
        onContinue={onContinue}
        onBack={onBack}
        title={translations.matchColumnsStep.nextButtonTitle}
        backTitle={translations.matchColumnsStep.backButtonTitle}
        trackStepName="Map the fields"
      />
    </>
  );
};

function getItemGridColumn(index: number) {
  return `${index + 1}/${index + 2}`;
}

function getHeaderGridColumn(columns: unknown[]) {
  return `1/${columns.length + 1}`;
}

const StyledSubTitle = styled(Typography)`
  && {
    margin-right: 1rem;
  }
`;
